import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import AgentDirectorySearch, { useDirectorySearchActions } from "@/components/AgentDirectorySearch";
import { DirectoryPanel } from "@/components/AgentDirectory";
import DirectoryBreadcrumb, { DIRECTORY_PATH, glue, statePath, townPath } from "@/components/DirectoryBreadcrumb";
import DirectoryMarketCards, { MarketCardsSkeleton, marketsFromFeatured } from "@/components/DirectoryMarketCards";
import { isNotFound, useDirectoryState } from "@/hooks/useAgentDirectory";
import "./DirectoryStateView.scss";
import AppLink from "@/components/AppLink";

export const groupTowns = (towns) => {
  const groups = [];
  [...(towns || [])]
    .sort((a, b) => String(a.name).localeCompare(String(b.name)))
    .forEach((town) => {
      const letter = String(town.name || "").charAt(0).toUpperCase();
      const key = /[A-Z]/.test(letter) ? letter : "#";
      const last = groups[groups.length - 1];
      if (last && last.letter === key) last.towns.push(town);
      else groups.push({ letter: key, towns: [town] });
    });
  return groups;
};

const anchorOf = (letter) => `towns-${letter === "#" ? "other" : letter.toLowerCase()}`;

const TownsSkeleton = () => (
  <ul className="directory-towns__list" aria-hidden="true">
    {Array.from({ length: 12 }, (_, index) => (
      <li key={index}>
        <span className="directory-towns__bar" />
      </li>
    ))}
  </ul>
);

const DirectoryStateView = ({ stateSlug, location }) => {
  const actions = useDirectorySearchActions();
  const query = useDirectoryState(stateSlug);
  const data = query.data;
  const state = data?.state;
  const name = state?.name || String(stateSlug || "").toUpperCase();
  const groups = useMemo(() => groupTowns(data?.towns), [data?.towns]);
  const markets = marketsFromFeatured(data?.featured, state?.code);
  const trail = [
    { label: "Agents", href: DIRECTORY_PATH },
    { label: name, href: statePath(stateSlug) },
  ];

  if (query.isError && !data) {
    const missing = isNotFound(query.error);
    return (
      <>
        <Helmet>
          <title>Agent directory | Gitcha</title>
          {missing ? <meta name="robots" content="noindex" /> : null}
        </Helmet>
        <DirectoryPanel
          title={missing ? "Gitcha does not list agents in this state yet" : "This state did not load"}
          body={
            missing
              ? "Pick one of the states Gitcha serves from the agent directory."
              : "Check your connection and try again."
          }
          actions={
            missing
              ? [{ label: "Browse by state", href: DIRECTORY_PATH }]
              : [
                  { label: "Try again", onClick: () => query.refetch() },
                  { label: "Agent directory", href: DIRECTORY_PATH },
                ]
          }
        />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Real estate agents in ${name} | Gitcha agent directory`}</title>
        <meta
          name="description"
          content={`Browse real estate agents in ${name} by city on Gitcha. Agents working with buyers right now are listed first.`}
        />
      </Helmet>
      <DirectoryBreadcrumb trail={trail} />
      <header className="directory-page__head">
        <h1>{glue(`Real estate agents in ${name}`)}</h1>
        <p>Pick a city to see the agents based there and nearby, with the buyer demand each one is working&nbsp;with.</p>
      </header>

      <AgentDirectorySearch location={location} {...actions} />

      {query.isLoading ? (
        <section className="directory-section" aria-busy="true">
          <h2>Featured markets</h2>
          <MarketCardsSkeleton count={2} />
        </section>
      ) : markets.length ? (
        <section className="directory-section" aria-labelledby="state-featured">
          <h2 id="state-featured">Featured markets</h2>
          <DirectoryMarketCards markets={markets} />
        </section>
      ) : null}

      <section className="directory-section directory-towns" aria-labelledby="state-towns">
        <h2 id="state-towns">{glue(`All cities in ${name}`)}</h2>
        {query.isLoading ? (
          <TownsSkeleton />
        ) : groups.length ? (
          <>
            <nav className="directory-towns__letters" aria-label="Jump to letter">
              {groups.map((group) => (
                <a key={group.letter} href={`#${anchorOf(group.letter)}`}>
                  {group.letter}
                </a>
              ))}
            </nav>
            {groups.map((group) => (
              <div className="directory-towns__group" key={group.letter} id={anchorOf(group.letter)}>
                <h3>{group.letter}</h3>
                <ul className="directory-towns__list">
                  {group.towns.map((town) => (
                    <li key={town.slug}>
                      <AppLink href={townPath(stateSlug, town.slug)}>{town.name}</AppLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        ) : (
          <p className="directory-section__quiet">
            Cities in {name} show here as agents join. <AppLink href={DIRECTORY_PATH}>Browse other states</AppLink>
          </p>
        )}
      </section>
    </>
  );
};

export default DirectoryStateView;
